import { useEffect } from "react";
import Footer from "@/components/landing/layout/Footer";
import Header from "@/components/landing/layout/Header";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

const claimExamples = [
  {
    scenario:
      "Urban Flux t-shirt stained permanently from coffee spill at café.",
    covered: "Yes",
    reason: "Single accidental spill covered if not misuse.",
  },
  {
    scenario: "Urban Flux hoodie torn by pet running into you at home.",
    covered: "Yes",
    reason: "Pet-related accidental damage covered.",
  },
  {
    scenario: "Urban Flux joggers ripped while traveling due to snag on bench.",
    covered: "Yes",
    reason: "Sudden accidental tear covered.",
  },
  {
    scenario: "Urban Flux t-shirt faded from repeated washing over months.",
    covered: "No",
    reason: "Gradual wear & tear excluded.",
  },
  {
    scenario: "Apparel stolen from residence after delivery.",
    covered: "No",
    reason: "Not covered under accidental damage plan.",
  },
  {
    scenario: "Cap damaged during self-repair attempt.",
    covered: "No",
    reason: "Unauthorized repair voids coverage.",
  },
];

const TermsNCondition: React.FC = () => {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        <section className="tw-bg-white tw-font-mori tw-max-w-5xl tw-mx-auto tw-px-6 tw-py-12 tw-text-[#1C1E21] tw-leading-relaxed">
          {/* Title */}
          <h1 className="md:tw-text-3xl tw-text-2xl tw-font-bold tw-mb-1">
            Protega Accidental Damage Protection Plan
          </h1>
          <p className="tw-text-sm tw-mb-8">
            For Products Purchased on Urban Flux
          </p>

          {/* 1. Introduction */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              1. Introduction
            </h2>
            <p className="tw-mb-4">
              Welcome, and thank you for choosing the Protega Accidental Damage
              Protection Plan. This document describes your complete set of
              coverage benefits, claim procedures, eligibility, exclusions, your
              rights as a consumer, and all legal and privacy terms.
            </p>
            <p className="tw-mb-4">
              This Protection Plan is an optional, paid service contract. It is
              not an insurance policy, nor is it an extended warranty or repair
              contract. It offers additional protection and is not a substitute
              for any manufacturer warranty, retailer statutory guarantee, or
              consumer protection law.
            </p>
            <p className="tw-text-sm">
              Governing Law: Laws of India
              <br />
              Jurisdiction: Mumbai High Court (exclusive)
              <br />
              Administered by: EmbedMike India Pvt. Ltd. (“Protega”)
              <br />
              Merchant: Urban Flux
            </p>
          </section>

          {/* 2. Definitions */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              2. Definitions
            </h2>

            <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
              <table className="tw-w-full md:tw-text-sm tw-text-xs tw-table-fixed">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-p-3 tw-border-r tw-text-left tw-w-1/3">
                      Term
                    </th>
                    <th className="tw-p-3 tw-text-left">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Customer</td>
                    <td className="tw-p-3">
                      End-user who purchases a product from Urban Flux and opts
                      for Protega Protection at checkout.
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Merchant</td>
                    <td className="tw-p-3">Urban Flux</td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Protega</td>
                    <td className="tw-p-3">
                      EmbedMike India Pvt. Ltd., providing the Protection Plan
                      and claims services.
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Product</td>
                    <td className="tw-p-3">
                      Any eligible item purchased from Urban Flux.
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">
                      Accidental Damage (AD)
                    </td>
                    <td className="tw-p-3">
                      Physical damage from an unexpected, external,
                      unintentional event (e.g. drops, liquid spills, pet
                      damage).
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Protection Plan</td>
                    <td className="tw-p-3">
                      The plan purchased: AD (1/2 yrs) as shown on covered
                      invoice.
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Sum Insured</td>
                    <td className="tw-p-3">
                      The invoice value of product (exclusive of taxes and
                      fees).
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Policy Period</td>
                    <td className="tw-p-3">12 or 24 months from delivery date.</td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Claim</td>
                    <td className="tw-p-3">
                      Customer’s request for repair, replacement, or
                      reimbursement under this Plan.
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Claim Limit</td>
                    <td className="tw-p-3">
                      One (1) valid claim per eligible Product per Policy
                      Period, payout up to purchase price.
                    </td>
                  </tr>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Eligible</td>
                    <td className="tw-p-3">
                      Only products & categories listed for protection on the
                      Urban Flux website{" "}
                      <span className="tw-underline">www.urban-flux.com</span>{" "}
                      are covered.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 3 - 5 */}
          <section className="tw-mb-8 tw-space-y-6">
            <div>
              <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
                3. Plan Scope & Key Features
              </h2>
              <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
                <li>
                  Accidental Damage (AD): Physical, visible damage from an
                  unexpected event post-delivery.
                </li>
                <li>
                  Includes single drops, liquid spills (excluding misuse), and
                  pet accidents.
                </li>
                <li>
                  Applies only after successful delivery and for normal consumer
                  use.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
                4. What is Covered
              </h2>
              <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
                <li>
                  Single-incident, visible accidental damage including tears
                  from drops or falls.
                </li>
                <li>
                  Accidental liquid spills and stains, not from misuse or
                  repeated exposure.
                </li>
                <li>Pet-related accidental damage and breakage.</li>
                <li>
                  All incidents must occur after delivery while in routine
                  personal use.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
                5. What is Not Covered (Exclusions)
              </h2>
              <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
                <li>
                  Products not purchased from Urban Flux or ineligible
                  categories.
                </li>
                <li>Damage existing or noticed before you purchased the Plan.</li>
                <li>Intentional act, gross negligence, willful or abusive use.</li>
                <li>
                  Using the Product outside normal consumer use (e.g. rental,
                  commercial).
                </li>
                <li>
                  Manufacturing defects or failures covered by original
                  warranty.
                </li>
                <li>
                  Cosmetic damage not impairing use (scratches, dents, fading,
                  discoloration).
                </li>
                <li>
                  Consumables (e.g. detachable accessories, unless specifically
                  included).
                </li>
                <li>
                  Normal wear and tear, corrosion, gradual deterioration or
                  long-term exposure.
                </li>
                <li>Unauthorized or self-repair.</li>
                <li>Damage after product re-sale.</li>
                <li>
                  Natural disasters (fire, flood, earthquake), war, riot,
                  pollution or infestation.
                </li>
                <li>
                  Software/data loss, network disruptions, or failures not
                  related to physical damage.
                </li>
                <li>
                  Failure to provide required documents for claims, or claim
                  after policy expires.
                </li>
                <li>Accumulated damage from repeated small accidents.</li>
              </ul>
            </div>
          </section>

          {/* 6. Filing a Claim */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              6. Filing a Claim
            </h2>
            <ol className="tw-list-decimal tw-list-inside tw-text-sm tw-space-y-2">
              <li>
                Contact Protega via support@protega.ai or visit{" "}
                <span className="tw-font-medium">
                  www.protega.ai/customer-login
                </span>
                .
              </li>
              <li>Provide policy certificate and purchase invoice.</li>
              <li>Share product photos (damage evidence).</li>
              <li>
                Provide details: date, time, location, description of the event.
              </li>
              <li>Do not ship product until instructed.</li>
              <li>
                Protega may offer repair, replacement, or payout at their
                discretion (up to the Sum Insured).
              </li>
              <li>
                Only one claim allowed per product/plan; after
                repair/payout/replacement your Plan expires.
              </li>
              <li>Unauthorized repair voids coverage.</li>
            </ol>
          </section>

          {/* 7. Cancellations & Refunds */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              7. Cancellations & Refunds
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-2">
              <li>Customer can cancel anytime.</li>
              <li>Within 30 days and no claim filed → 100% refund.</li>
              <li>
                Provider can cancel only for fraud, misrepresentation, or
                non-payment.
              </li>
              <li>Refunds will be processed within 30 days.</li>
            </ul>
          </section>

          {/* 8. Legal Terms & General Conditions */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              8. Legal Terms & General Conditions
            </h2>
            <ul className="tw-text-sm tw-space-y-2 tw-list-inside tw-list-disc">
              <li>
                <strong>Jurisdiction:</strong> Laws of India, exclusive Mumbai
                court jurisdiction.
              </li>
              <li>
                <strong>Dispute Resolution:</strong> Individual arbitration per
                Arbitration and Conciliation Act, 1996.
              </li>
              <li>
                <strong>Whole Agreement:</strong> This document and your
                purchase confirmation are the only binding agreement.
              </li>
              <li>
                <strong>Privacy:</strong> Data handled as per Protega's Privacy
                Policy.
              </li>
              <li>
                <strong>Limitation of Liability:</strong> No party
                (Protega/Urban Flux) is liable for indirect, incidental,
                punitive, or consequential damages.
              </li>
            </ul>
          </section>

          {/* 9. FAQ */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">9. FAQ</h2>
            <div className="tw-text-sm tw-space-y-3">
              <div>
                <p className="tw-font-semibold">
                  Q: Is this plan the same as warranty or insurance?
                </p>
                <p>
                  A: No. It is an optional protection plan for accidental
                  events, separate from any manufacturer warranty or insurance.
                </p>
              </div>
              <div>
                <p className="tw-font-semibold">
                  Q: What do I do if my product is damaged?
                </p>
                <p>
                  A: File a claim with your documents and photos as soon as
                  possible via the contact above; do not attempt self-repair.
                </p>
              </div>
              <div>
                <p className="tw-font-semibold">Q: What is the policy period?</p>
                <p>A: Accidental Damage: 12 or 24 months from delivery.</p>
              </div>
              <div>
                <p className="tw-font-semibold">
                  Q: What happens once my claim is approved?
                </p>
                <p>
                  A: The product is collected for repair/replacement or you’re
                  paid (at Protega’s discretion). Plan ends after one approved
                  claim.
                </p>
              </div>
              <div>
                <p className="tw-font-semibold">Q: What is not covered?</p>
                <p>
                  A: Intentional damage, cosmetic flaws, business use,
                  unauthorized repairs, gradual wear and tear, and other
                  exclusions.
                </p>
              </div>
              <div>
                <p className="tw-font-semibold">
                  Q: Can I cancel the Plan anytime?
                </p>
                <p>A: Yes. Within 30 days with no claims → full refund.</p>
              </div>
              <div>
                <p className="tw-font-semibold">
                  Q: Can the Plan be transferred?
                </p>
                <p>A: No.</p>
              </div>
              <div>
                <p className="tw-font-semibold">Q: Is my data protected?</p>
                <p>
                  A: Yes. Protega processes data in line with its Privacy
                  Policy.
                </p>
              </div>
              <div>
                <p className="tw-font-semibold">Q: How do I track claim status?</p>
                <p>
                  A: You’ll be updated at each stage by email/SMS after filing a
                  claim.
                </p>
              </div>
              <div>
                <p className="tw-font-semibold">
                  Q: What if damage arises from misuse?
                </p>
                <p>
                  A: Damage from misuse, repeated small accidents, or neglect is
                  excluded.
                </p>
              </div>
            </div>
          </section>

          {/* 10. Claim Examples */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              10. Filing a Claim (Examples)
            </h2>

            <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
              <table className="tw-w-full md:tw-text-sm tw-text-xs tw-table-fixed">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-p-3 tw-border-r tw-text-left md:tw-w-1/2">
                      Scenario
                    </th>
                    <th className="tw-p-3 tw-border-r tw-text-left">Covered?</th>
                    <th className="tw-p-3 tw-text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {claimExamples.map((ex, i) => (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50"}
                    >
                      <td className="tw-p-3 tw-border-r">{ex.scenario}</td>
                      <td className="tw-p-3 tw-border-r">{ex.covered}</td>
                      <td className="tw-p-3">{ex.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 11. Service Commitment */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              11. Service Commitment
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-2">
              <li>
                Policy document: Delivered by email after policy payment.
              </li>
              <li>
                Claims handling: Rapid response, product pickup within 24 hours
                after claim approval.
              </li>
              <li>
                Customer service: Dedicated Protega and Urban Flux support via
                email/phone.
              </li>
            </ul>
          </section>

          {/* 12. Contact Us */}
          <section className="tw-mb-12">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              12. Contact Us
            </h2>

            <div className="tw-text-sm tw-space-y-2">
              <p>
                <strong>Protega Claims & Support:</strong>
              </p>
              <p>
                Email:{" "}
                <a className="tw-underline" href="mailto:support@protega.ai">
                  support@protega.ai
                </a>
              </p>
              <p>
                Web:{" "}
                <a
                  className="tw-underline"
                  href="https://www.protega.ai/customer-login"
                >
                  www.protega.ai/customer-login
                </a>
              </p>

              <div className="tw-pt-3">
                <p>
                  <strong>Urban Flux Support:</strong>
                </p>
                <p>
                  Email:{" "}
                  <a
                    className="tw-underline"
                    href="mailto:support@urban-flux.com"
                  >
                    support@urban-flux.com
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Back link */}
          <Link
            to={"/urbanFlux"}
            className="tw-border-t tw-border-gray-200 tw-pt-6 tw-flex tw-items-center tw-text-sm tw-text-gray-800"
          >
            <ChevronLeft /> Back to Urban Flux Care
          </Link>
        </section>
        <Footer />
      </main>
    </div>
  );
};

export default TermsNCondition;
